import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Check } from 'lucide-react';
// Internal-only typography experiment: recreates the REAL Giga home hero
// in-context and lets the team flip its headline typeface between today's
// DM Sans and a Claude-inspired type system, so we can judge whether
// "Claude's font" feels better than what we run now.
//
// "Claude's font" = Anthropic's brand stack. They use Styrene (a clean
// geometric grotesque) for UI/display and a warm Tiempos-style serif for
// reading text — the serif being the most recognizable. Since both are
// proprietary, we preview the closest freely-available Google Fonts matches:
//   • Styrene  → Hanken Grotesk
//   • Tiempos  → Source Serif 4 (primary) / Newsreader (alt)
type FontChoice = {
  id: string;
  label: string;
  tagline: string;
  // Applied to the big display headline + section headings.
  headlineStack: string;
  headlineWeight: number;
  // Applied to the eyebrow / supporting UI labels.
  uiStack: string;
  current?: boolean;
};
const CHOICES: FontChoice[] = [{
  id: 'current',
  label: 'DM Sans',
  tagline: 'What the home screen runs today.',
  headlineStack: '"DM Sans", Inter, sans-serif',
  headlineWeight: 700,
  uiStack: '"Roboto Mono", ui-monospace, monospace',
  current: true
}, {
  id: 'claude-serif',
  label: 'Claude — Serif',
  tagline: "Anthropic's warm Tiempos-style serif (Source Serif 4). The look most people recognize as Claude.",
  headlineStack: '"Source Serif 4", Georgia, serif',
  headlineWeight: 600,
  uiStack: '"Hanken Grotesk", Inter, sans-serif'
}, {
  id: 'claude-serif-alt',
  label: 'Claude — Serif (Newsreader)',
  tagline: 'A slightly more editorial Tiempos alternative, with a touch more contrast.',
  headlineStack: '"Newsreader", Georgia, serif',
  headlineWeight: 600,
  uiStack: '"Hanken Grotesk", Inter, sans-serif'
}, {
  id: 'claude-sans',
  label: 'Claude — Sans (Styrene)',
  tagline: "Claude's clean geometric UI sans (Styrene), approximated with Hanken Grotesk.",
  headlineStack: '"Hanken Grotesk", Inter, sans-serif',
  headlineWeight: 800,
  uiStack: '"Hanken Grotesk", Inter, sans-serif'
}];
const LOGOS = ['Shopify', 'Notion', 'Ramp', 'Tonic', 'Linear', 'Ember', 'Stripe', 'Entropy Studios', 'Figma', 'Mercury', 'Vercel'];
// Representative real home-page section headlines, shown beneath the hero so
// the face is judged across more than one line of copy.
const SECTION_LINES = ['Just @mention Giga in any chat', 'One Giga. Every part of your life.', 'Stop paying the context-switch tax'];
// A faithful recreation of the live MarketingHero, but with the headline /
// eyebrow typefaces driven by the selected choice so it reads exactly like the
// real home screen would with that font applied.
function HeroPreview({
  choice
}: {
  choice: FontChoice;
}) {
  return <section className="relative w-full overflow-hidden bg-[#FAF8F4] rounded-3xl border border-neutral-200/70" data-id="element-3837">
      {/* Halftone dot-grid texture, matching the live hero. */}
      <div aria-hidden="true" className="cream-grid z-0" data-id="element-3838" />

      <div className="relative z-20 flex flex-col justify-center" data-id="element-3839">
        <div className="w-full max-w-[1100px] mx-auto flex flex-col justify-center px-6 md:px-12 pt-16 md:pt-24 pb-14 md:pb-20" data-id="element-3840">
          <span className="text-xs sm:text-sm font-medium uppercase tracking-[0.28em] text-neutral-500 mb-4 md:mb-5 block" style={{
          fontFamily: choice.uiStack
        }} data-id="element-3841">
            AI Co-Pilot for Teams
          </span>

          <h1 className="text-[#2B211A] tracking-tight leading-[0.95] text-4xl sm:text-6xl lg:text-[80px] max-w-4xl" style={{
          fontFamily: choice.headlineStack,
          fontWeight: choice.headlineWeight
        }} data-id="element-3842">
            Everyone's new <br className="hidden md:block" data-id="element-3843" />
            favorite <span className="text-[#EA580C]" data-id="element-3844">colleague</span>
          </h1>

          <div className="mt-8 md:mt-10 flex flex-col items-start gap-6" data-id="element-3845">
            <span className="bg-[#EA580C] text-white px-9 py-4 rounded-full font-bold text-lg shadow-lg shadow-[#EA580C]/20" data-id="element-3846">
              Try Giga for free
            </span>
            <div className="flex items-center gap-2 md:gap-3 text-neutral-500" data-id="element-3847">
              <div className="flex gap-0.5 text-[#EA580C]" data-id="element-3848">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-3.5 h-3.5 md:w-4 md:h-4" fill="currentColor" strokeWidth={0} data-id="element-3849" />)}
              </div>
              <span className="text-xs md:text-sm tracking-tight" style={{
              fontFamily: choice.uiStack
            }} data-id="element-3850">
                4.9 · loved by 1,200+ teams
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Logo marquee strip, mirroring the live hero footer. */}
      <div className="relative z-30 pb-7 md:pb-9 pointer-events-none" data-id="element-3851">
        <p className="text-center text-[10px] sm:text-xs uppercase tracking-[0.28em] text-[#2B211A]/70 mb-4" style={{
        fontFamily: choice.uiStack
      }} data-id="element-3852">
          Trusted by fast-moving teams
        </p>
        <div className="relative overflow-hidden" style={{
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 12%, black 88%, transparent)',
        maskImage: 'linear-gradient(to right, transparent, black 12%, black 88%, transparent)'
      }} data-id="element-3853">
          <div className="flex w-max items-center gap-x-10 md:gap-x-14 px-6" data-id="element-3854">
            {LOGOS.map((logo, i) => <span key={`${logo}-${i}`} className="text-base md:text-lg font-bold tracking-tight text-[#2B211A]/55 whitespace-nowrap" style={{
            fontFamily: choice.headlineStack
          }} data-id="element-3855">
                {logo}
              </span>)}
          </div>
        </div>
      </div>
    </section>;
}
export function ClaudeFont() {
  const [activeId, setActiveId] = useState<string>('claude-serif');
  const active = CHOICES.find(c => c.id === activeId) ?? CHOICES[0];
  return <div className="min-h-screen bg-white pt-32 pb-24" data-id="element-3856">
      <div className="max-w-[1180px] mx-auto px-6 md:px-10" data-id="element-3857">
        {/* Header */}
        <div className="mb-10" data-id="element-3858">
          <p className="text-[11px] font-bold uppercase tracking-[4px] text-[#EA580C] mb-4" data-id="element-3859">
            Internal · Typography
          </p>
          <h1 className="text-4xl md:text-5xl font-title font-bold text-[#2B211A] tracking-tight mb-4" data-id="element-3860">
            Trying Claude's font on the home screen
          </h1>
          <p className="text-lg text-neutral-500 max-w-2xl leading-relaxed" data-id="element-3861">
            Claude's brand uses{' '}
            <span className="font-semibold text-[#2B211A]" data-id="element-3862">Styrene</span> (a
            clean geometric sans) for UI and a warm{' '}
            <span className="font-semibold text-[#2B211A]" data-id="element-3863">
              Tiempos-style serif
            </span>{' '}
            for reading text — the serif being what most people recognize. Below
            is your actual home hero, rendered with each option so you can judge
            it in context. Flip between them to compare against today's DM Sans.
          </p>
        </div>

        {/* Font switcher */}
        <div className="flex flex-wrap gap-2.5 mb-8" data-id="element-3864">
          {CHOICES.map(choice => {
          const isActive = choice.id === activeId;
          return <button key={choice.id} onClick={() => setActiveId(choice.id)} className={`group inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold transition-all ${isActive ? 'border-[#EA580C] bg-[#EA580C] text-white shadow-sm' : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:text-[#2B211A]'}`} data-id="element-3865">
                {isActive && <Check size={14} strokeWidth={3} data-id="element-3866" />}
                {choice.label}
                {choice.current && <span className={`text-[10px] font-bold uppercase tracking-wider ${isActive ? 'text-white/80' : 'text-neutral-400'}`} data-id="element-3867">
                    Live
                  </span>}
              </button>;
        })}
        </div>

        {/* Active choice description */}
        <p className="text-sm text-neutral-500 mb-5 max-w-2xl leading-relaxed" data-id="element-3868">
          {active.tagline}
        </p>

        {/* In-context hero preview */}
        <motion.div key={active.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-3869">
          <HeroPreview choice={active} data-id="element-3870" />
        </motion.div>

        {/* Supporting section headlines from the live home page */}
        <div className="mt-10 rounded-3xl border border-neutral-200/70 bg-white overflow-hidden" data-id="element-3871">
          <div className="px-6 md:px-8 py-4 border-b border-neutral-100 bg-[#FAF8F4]" data-id="element-3872">
            <p className="text-[11px] font-bold uppercase tracking-[3px] text-neutral-400" data-id="element-3873">
              Section headlines · {active.label}
            </p>
          </div>
          <div className="px-6 md:px-10 py-8 md:py-10 space-y-8" data-id="element-3874">
            {SECTION_LINES.map(line => <p key={line} className="text-2xl md:text-4xl text-[#2B211A] tracking-tight leading-[1.1]" style={{
            fontFamily: active.headlineStack,
            fontWeight: active.headlineWeight
          }} data-id="element-3875">
                {line}
              </p>)}
          </div>

          {/* On the dark brand surface too, since several home headlines sit on
           the espresso stage. */}
          <div className="px-6 md:px-10 pb-10" data-id="element-3876">
            <div className="rounded-2xl bg-[#2B211A] px-6 md:px-8 py-8" data-id="element-3877">
              <p className="text-[11px] font-bold uppercase tracking-[3px] text-white/40 mb-4" data-id="element-3878">
                On dark
              </p>
              <p className="text-2xl md:text-4xl text-white tracking-tight leading-[1.1] mb-3" style={{
              fontFamily: active.headlineStack,
              fontWeight: active.headlineWeight
            }} data-id="element-3879">
                Stop paying the context-switch tax
              </p>
              <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-xl font-body" data-id="element-3880">
                Body copy stays Inter — only the headline face changes, exactly
                as it would on the live home screen.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}